import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';

export interface StaffStatusDto {
  statusMerchantStaffId: number;
  code: string;
  name: string;
  isActive: boolean;
  displayOrder: number;
}

export interface LocationStaffAdminDto {
  merchantStaffId: string;
  merchantLocationId: string;
  employeeExternalId?: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email?: string;
  phone?: string;
  statusMerchantStaffId: number;
  statusName: string;
  calendarColor?: string;
  assignedConceptsCount: number;
}

export interface CreateLocationStaffRequest {
  employeeExternalId?: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email?: string;
  phone?: string;
  statusMerchantStaffId: number;
  calendarColor?: string;
}

export interface UpdateLocationStaffRequest {
  firstName: string;
  lastName: string;
  displayName: string;
  email?: string;
  phone?: string;
  statusMerchantStaffId: number;
  calendarColor?: string;
}

export interface LocationStaffCreatedResponse {
  merchantStaffId: string;
  merchantLocationId: string;
  displayName: string;
}

export interface LocationStaffUpdatedResponse {
  merchantStaffId: string;
  displayName: string;
}

export interface LocationStaffAdminResponse {
  staff: LocationStaffAdminDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SingleResponse<T> {
  isSuccess: boolean;
  value?: T;
  error?: {
    statusCode: number;
    messages: string[];
  };
}

export interface PaginableResponse<T> {
  isSuccess: boolean;
  values?: T[];
  total?: number;
  error?: {
    statusCode: number;
    messages: string[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class LocationStaffService {
  private apiUrl = `${environment.apiUrl}/api/v2/booking/staff`;

  constructor(private http: HttpClient) {}

  getStaffStatuses(): Observable<PaginableResponse<StaffStatusDto>> {
    return this.http.get<PaginableResponse<StaffStatusDto>>(`${this.apiUrl}/statuses`);
  }

  searchLocationStaff(
    locationId: string,
    searchTerm?: string,
    statusId?: number,
    page: number = 1,
    pageSize: number = 20
  ): Observable<SingleResponse<LocationStaffAdminResponse>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (searchTerm) {
      params = params.set('search', searchTerm);
    }

    if (statusId !== undefined && statusId !== null) {
      params = params.set('statusId', statusId.toString());
    }

    return this.http.get<SingleResponse<LocationStaffAdminResponse>>(
      `${this.apiUrl}/locations/${locationId}`,
      { params }
    );
  }

  createLocationStaff(
    locationId: string,
    request: CreateLocationStaffRequest
  ): Observable<SingleResponse<LocationStaffCreatedResponse>> {
    return this.http.post<SingleResponse<LocationStaffCreatedResponse>>(
      `${this.apiUrl}/locations/${locationId}`,
      request
    );
  }

  updateLocationStaff(
    locationId: string,
    staffId: string,
    request: UpdateLocationStaffRequest
  ): Observable<SingleResponse<LocationStaffUpdatedResponse>> {
    return this.http.put<SingleResponse<LocationStaffUpdatedResponse>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}`,
      request
    );
  }

  deleteLocationStaff(
    locationId: string,
    staffId: string
  ): Observable<SingleResponse<DeletedResponse>> {
    return this.http.delete<SingleResponse<DeletedResponse>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}`
    );
  }
}

export interface DeletedResponse {
  message: string;
}
